import * as React from 'react';
import { connect } from 'react-redux';
import { Select } from 'antd';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getContactLabelWithEmail } from '../../utils/DataFormatterUtils';
import {
	buyerContactsRestCrudThunksForBuyer,
	buyerContactsRestCrudThunksForSupplier,
} from '../../thunks/buyer_contacts_thunks';

// The people a deliverable can be assigned to, as emails — the buyer's contacts,
// paged and searched server-side (OWAsyncSelect, never a bare read + client filter).
//
// Shared by the deliverable form and by the project template's default-assignee
// field, so the two pickers cannot drift on page size, label format or the value the
// form stores (an email, matching ProjectDeliverable.assignedTo).
//
// Rendered inside a Form.Item, so value/onChange arrive as props from antd — they are
// spread straight onto OWAsyncSelect rather than being held here.
//
// `multiple` (default) stores an array of emails; pass multiple={false} where the field
// holds a single email, as a project task's assignee does.

interface DeliverableAssigneeSelectProps {
	// Target collection for the contacts autocomplete. Distinct per call-site: a shared
	// one would let one picker's search truncate what another has loaded.
	targetCollectionName: string;
	placeholder?: string;
	// false for the one-assignee case (a project task has a single assignee, where a
	// deliverable has a list). The form value is then an email string, not an array.
	multiple?: boolean;
	// Injected by connect / withUserType.
	buyerContacts?: any;
	fetchContacts?: any;
	fetchMultipleContacts?: any;
	userType?: string;
}

const DeliverableAssigneeSelect: React.FC<DeliverableAssigneeSelectProps> = ({
	targetCollectionName,
	placeholder = 'Select people',
	multiple = true,
	buyerContacts,
	fetchContacts,
	fetchMultipleContacts,
	userType,
	...formItemProps
}) => (
	<OWAsyncSelect
		{...formItemProps}
		mode={multiple ? 'multiple' : undefined}
		placeholder={placeholder}
		style={{ width: '100%' }}
		stateSlice={buyerContacts}
		targetCollectionName={targetCollectionName}
		valueAccessor={(contact) => nullSafeGetOrElse('email', contact, '')}
		labelAccessor={(contact) => getContactLabelWithEmail(contact)}
		// The only adaptation OWAsyncSelect needs: its first argument is the raw search
		// text, the thunk's is a params object. Everything after it passes straight
		// through — dropping pagination or addToTargetCollection here is what previously
		// made load-more re-request page one.
		fetchData={(search, collectionName, pagination, sorting, filters, addToTargetCollection) =>
			fetchContacts({ search }, collectionName, pagination, sorting, filters, addToTargetCollection)
		}
		fetchMultiple={(ids, collectionName) => fetchMultipleContacts(ids, collectionName)}
		renderRecord={(contact) => (
			<Select.Option
				key={nullSafeGetOrElse('email', contact, '')}
				value={nullSafeGetOrElse('email', contact, '')}
			>
				{getContactLabelWithEmail(contact)}
			</Select.Option>
		)}
	/>
);

const mapStateToProps = (state) => ({
	buyerContacts: state.buyer_contacts,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	// A straight pass-through to read(). In particular pagination goes in as its own
	// argument rather than as limit/offset in params: fetchSuccess writes
	// `{ ...pagination, total }` into the target collection, so losing it here drops
	// current/pageSize and OWAsyncSelect.handleLoadMore — which reads them back to work out
	// the next page — computes `undefined + 1` and never fetches again. The thunk derives
	// limit/offset from the pagination itself.
	fetchContacts: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			(ownProps.userType === ROLE_TYPES.SUPPLIER
				? buyerContactsRestCrudThunksForSupplier
				: buyerContactsRestCrudThunksForBuyer
			).read(params, targetCollectionName, pagination, sorting, filters, addToTargetCollection)
		),
	fetchMultipleContacts: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? buyerContactsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: buyerContactsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(DeliverableAssigneeSelect)
);
